/**
 * Store réactif de l'inbox.
 *
 * `threads` est la liste enrichie (avec headers + snippet) prête pour l'UI.
 * Pas de cache local en Phase 4a — tout en mémoire, refetch on focus.
 */

import type { GmailThread, GmailMessage } from "./api";

// Re-export pour le composant ThreadView qui veut typer ses props
export type { GmailThread, GmailMessage };

export interface InboxRow {
  thread: GmailThread;
  fromName: string;
  fromEmail: string;
  subject: string;
  snippet: string;
  /** Unix ms du dernier message — sert au tri DESC + à la date affichée. */
  lastDate: number;
  unread: boolean;
}

/** Pièce jointe à l'envoi (en mémoire, pré-encoding). */
export interface DraftAttachment {
  /** Identifiant local pour suppression UI (UUID frontend) */
  uid: string;
  filename: string;
  mimeType: string;
  /** Taille en bytes — affiché dans la chip + check du cap 25 MB */
  size: number;
  /** Bytes à encoder base64 dans le multipart/mixed au send */
  bytes: Uint8Array<ArrayBuffer>;
}

/** État de la modale Compose. null = fermée. */
export interface ComposeDraft {
  to: string;          // comma-separated, l'user édite
  cc: string;
  bcc: string;
  subject: string;
  body: string;
  showCcBcc: boolean;
  /** True = inject tracking pixel + POST /me/pixels au send. Default true. */
  track: boolean;
  /** Files joints. Vide par défaut. */
  attachments: DraftAttachment[];
  // Champs de threading (replies) — undefined pour nouveau mail
  inReplyTo?: string;
  references?: string[];
  threadId?: string;
  /** Pour afficher "Replying to: X" en haut du compose */
  replyToFromLabel?: string;
}

/**
 * Filtre actif pour la liste inbox. Les filter pills et la searchbar
 * mutent ces champs ; loadInbox les lit pour construire la query Gmail.
 *
 *  - kind     : filter sémantique mappé sur des labels Gmail standards
 *  - labelIds : override custom si user a tap un split `[NaoMail]/*`
 *  - query    : searchbar. null = pas de search active.
 *
 * Le mapping kind → query Gmail est dans `loadInbox`. "all" laisse
 * vide pour récupérer tous les threads. "unread" applique `is:unread`
 * pour avoir les non-lus de toutes les boîtes.
 */
export type FilterKind =
  | "inbox"      // INBOX label
  | "all"        // Tous les mails (pas de filter label)
  | "sent"       // SENT label
  | "unread";    // Non lus (toutes boîtes)

export interface InboxFilter {
  kind: FilterKind;
  labelIds: string[];
  query: string | null;
}

interface GmailState {
  rows: InboxRow[];
  /** Token Gmail pour pagination — si présent, "Load more" possible. */
  nextPageToken: string | null;
  loading: boolean;
  loadingMore: boolean;
  lastSyncAt: number | null;
  error: string | null;
  /** True si on a détecté un scope manquant et qu'il faut prompter re-sign-in. */
  needsReauth: boolean;
  /** Phase 4b — thread ID actuellement ouvert dans ThreadView. null = inbox list. */
  selectedThreadId: string | null;
  /** Thread complet en cours de lecture. Refetch au changement de selectedThreadId. */
  currentThread: GmailThread | null;
  loadingThread: boolean;
  threadError: string | null;
  /** Phase 5 — Compose modal */
  composeDraft: ComposeDraft | null;
  /** Phase C — filtre inbox (label split + search query) */
  filter: InboxFilter;
}

export const gmailState = $state<GmailState>({
  rows: [],
  nextPageToken: null,
  loading: false,
  loadingMore: false,
  lastSyncAt: null,
  error: null,
  needsReauth: false,
  selectedThreadId: null,
  currentThread: null,
  loadingThread: false,
  threadError: null,
  composeDraft: null,
  filter: { kind: "inbox", labelIds: ["INBOX"], query: null },
});
